import { BreadcrumbComponent, Button, CIActionGuard, CIPageHeader, Loader, Modal, MyIcon, toLocalDate, useToastMessage, useUserPermission } from 'ccs-ui-components';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { requiredAuthPermission } from '../../../../common/constants/UserPermissionConstants';
import { AppDispatch } from '../../../store/AppStore';
import {
  getBannerDataRequest
} from '../../../store/actions/sagaActions/schedule-maintenance/GetBannerDataSagaActions';
import { MaintenanceList } from '../maintenance/scheduled-maintenance-list/MaintenanceList';
import classes from './ScheduleMaintenanceWrapper.module.scss';
import { clearSecretServiceCache } from '../api';

export const ConfigurationWrapper = () => {
  const { t: translate } = useTranslation();
  const { permissionObject } = useUserPermission();
  const [showBanner, setShowBanner] = useState(true);
  const dispatch = useDispatch<AppDispatch>();
  const [alertNumber, setAlertNumber] = useState(1);
  const [showImpactedServicesModal, setShowImpactedServicesModal] = useState(false);
  const [showCacheModal, setShowCacheModal] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const { success: successToast, failure: failureToast, renderToast } = useToastMessage();

  const leftIconClickHandler = () => {
    if (alertNumber > 1) setAlertNumber(alertNumber - 1);
    else setAlertNumber(BannerDataDetails?.length);
  };

  const rightIconClickHandler = () => {
    if (alertNumber < BannerDataDetails?.length) setAlertNumber(alertNumber + 1);
    else setAlertNumber(1);
  };

  const showMoreHandler = () => {
    setShowImpactedServicesModal(true);
  };

  const { BannerDataDetails, isLoading, errorMessage } = useSelector(
    (state: any) => state?.BannerDataReducer
  );
  useEffect(() => {
    dispatch(getBannerDataRequest());
  }, [dispatch]);

  useEffect(() => {
    if (BannerDataDetails?.length > 0) {
      setShowBanner(true);
    } else {
      setShowBanner(false);
    }
  }, [BannerDataDetails]);

  const sendFormattedDate = (date: any) => <span>{moment(date).format('dddd').substring(0, 3)} {toLocalDate({ date })}</span>;

  const impactedComponentsModalTitle = (
    <div className={classes['impacted-modal-title-wrapper']}>
      <div className={classes['impacted-modal-title-header']}>{translate('configuration_wrapper.txt_impacted_components')}</div>
    </div>
  );

  const impactedComponentsModalBody = (
    <div className={classes['success-modal-body-wrapper']}>
      <div className={classes['impacted-modal-message']}>{BannerDataDetails[alertNumber - 1]?.message}</div>
      {BannerDataDetails.length && <div className={classes['impacted-modal-date-wrapper']}><span>{translate('configuration_wrapper.txt_scheduled_time')}: </span><span className={classes['impacted-modal-date']}>{sendFormattedDate(BannerDataDetails[alertNumber - 1]?.startTimestamp)} {translate('configuration_wrapper.txt_to')} {sendFormattedDate(BannerDataDetails[alertNumber - 1]?.endTimestamp)}</span></div>}
    </div>
  );

  const clearConfigCache = () => {
      setShowCacheModal(true);
  };

  const confirmClearCache = async () => {
    setShowCacheModal(false);
    setShowLoader(true);
    const resp = await clearSecretServiceCache();
    setShowLoader(false);

    if (resp?.status.toLowerCase() === 'success') {
          successToast(
            translate('toast_component.lbl_success'),
            resp?.message
          );
    } else {
          failureToast(
            translate('toast_component.lbl_error'),
            resp?.message || 'Error while clearing cache'
          );
    }
  };

  const cancelClearCache = () => {
    setShowCacheModal(false);
  };

  return (
    <>
      {(isLoading || showLoader) && <Loader show />}
      {renderToast()}
      {!errorMessage && !isLoading && showBanner && BannerDataDetails.length && (
        <div className={classes['maintenance-banner']}>
          <div className={` ${classes['maintenance-banner-content']} container`}>
            <MyIcon name="warningCircleFill" className={classes['warning-icon-style']} />
            <Button
              variant="tertiary"
              className={classes['icon-role-left-btn']}
              data-testid="left-icon"
              onClick={leftIconClickHandler}
              aria-label="leftIconClickHandler"
            >
              <i className="bi bi-chevron-left" />
            </Button>
            {alertNumber} of {BannerDataDetails?.length}
            <Button
              variant="tertiary"
              className={classes['icon-role-right-btn']}
              data-testid="right-icon"
              onClick={rightIconClickHandler}
              aria-label="rightIconClickHandler"
            >
              <i className="bi bi-chevron-right" />
            </Button>
            <span className={classes['main-name-header']}>{BannerDataDetails[alertNumber - 1]?.maintenanceName}</span>
            <span className={classes['main-text-header']}> : {translate('configuration_wrapper.txt_component_desc')} </span>
            <span className={classes['date-format-header']}>{sendFormattedDate(BannerDataDetails[alertNumber - 1]?.startTimestamp)} to {sendFormattedDate(BannerDataDetails[alertNumber - 1]?.endTimestamp)} </span>
            <Button
              variant="link"
              className={classes['show-more-btn']}
              data-testid="show-more"
              onClick={showMoreHandler}
            >{translate('configuration_wrapper.txt_show_more')}
            </Button>
          </div>
        </div>
      )}
      <div className={classes['dash-wrapper-without-padding']}>
        <BreadcrumbComponent />
        {CIActionGuard([requiredAuthPermission.Maintainence.edit, requiredAuthPermission.Maintainence.view], permissionObject.permission)
        && (
        <>
          <span className={classes['list-title']}>
            <CIPageHeader header={translate('clear_cache_section.txt_section_header')} />
          </span>
          <div className={classes.cache_header}>
            <div className={classes.cache_description}>{translate('clear_cache_section.txt_description')}</div>
            <div>
              <Button
                variant="primary"
                onClick={clearConfigCache}
                data-testid="cacheBtn"
              >
                {translate('clear_cache_section.txt_button')}
              </Button>
            </div>
          </div>
        </>
        )}
        <span className={classes['list-title']}>
          <CIPageHeader header={translate('configuration_wrapper.txt_maintenance')} />
        </span>
      </div>
      {CIActionGuard([requiredAuthPermission.Maintainence.edit, requiredAuthPermission.Maintainence.view], permissionObject.permission)
        && (
          <MaintenanceList />
        )}
      <Modal
        modalTitle={impactedComponentsModalTitle}
        modalBody={impactedComponentsModalBody}
        modalShow={showImpactedServicesModal}
        onCloseModal={() => setShowImpactedServicesModal(false)}
        alignToCentre
        disableSaveBtn
        disableCloseBtn
      />
      <Modal
        modalTitle={translate('clear_cache_section.txt_modal_title')}
        modalBody={translate('clear_cache_section.txt_modal_body')}
        modalShow={showCacheModal}
        btnSaveText={translate('clear_cache_section.txt_confirm') ?? ''}
        btnCloseText={translate('clear_cache_section.txt_cancel') ?? ''}
        onSaveModal={confirmClearCache}
        onCloseModal={cancelClearCache}
        alignToCentre
        disableSaveBtn={false}
        disableCloseBtn={false}
        type="warning"
      />
    </>
  );
};
